import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Input,
    NumberInput,
    Select,
    SelectItem,
    Textarea,
    Spinner,
    Divider,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
} from "@heroui/react";
import { LuArrowLeft, LuSave, LuPlus, LuTrash2 } from "react-icons/lu";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { useFetchData, usePostData } from "@/hooks";
import { ErrorState, PageHeader, ThemedText } from "@/components/ui";
import { showToast } from "@/utils";
import axios from "axios";
import { BASE_API } from "@/constants";

const MaterialFormulaCreate = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    // Fetch Master Data
    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses } = useFetchData("select/warehouse"); // Not needed for formula itself, but kept just in case layout changes
    // Unit fetch logic needs to be dynamic based on material

    // Fetch Initial Data for Edit
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(isEdit ? `material-formulas/${id}/edit` : null);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: {
            name: "",
            material_id: "",
            material_unit_id: "",
            quantity: null, // Default base qty suggestion
            description: "",
            items: [
                {
                    material_id: "",
                    material_unit_id: "",
                    quantity: null,
                },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items",
    });

    // Dynamic Unit Loading State
    const [unitsState, setUnitsState] = useState({
        parent: [], // Units for Header/Result Product
        parentLoading: false,
        items: {}, // Units for each ingredient item row: { [index]: [] }
        itemsLoading: {}, // { [index]: boolean }
    });

    const watchMaterialId = watch("material_id");
    const watchItems = watch("items");

    // Fetch parent units when material_id changes
    useEffect(() => {
        const fetchParentUnits = async () => {
            if (!watchMaterialId) {
                setUnitsState((prev) => ({
                    ...prev,
                    parent: [],
                    parentLoading: false,
                }));
                return;
            }
            setUnitsState((prev) => ({ ...prev, parentLoading: true }));
            try {
                const response = await axios.get(
                    `${BASE_API}/select/material-unit`,
                    { params: { material_id: watchMaterialId } },
                );
                const units = response.data.data || [];
                setUnitsState((prev) => ({
                    ...prev,
                    parent: units,
                    parentLoading: false,
                }));

                // Auto-select first unit if available and not set
                if (units.length > 0 && !isEdit) {
                    // Only auto-select on Create to avoid overwriting edit data
                    // Check if current value matches options, if not, set first
                    // For simplicity, just letting user pick or keeping current if valid
                }
            } catch (error) {
                console.error("Error fetching parent units:", error);
                setUnitsState((prev) => ({ ...prev, parentLoading: false }));
            }
        };
        fetchParentUnits();
    }, [watchMaterialId]); // Removing isEdit dependency to allow change

    // Fetch item units function
    const fetchItemUnits = async (index, materialId) => {
        if (!materialId) {
            setUnitsState((prev) => ({
                ...prev,
                items: { ...prev.items, [index]: [] },
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
            return;
        }
        setUnitsState((prev) => ({
            ...prev,
            itemsLoading: { ...prev.itemsLoading, [index]: true },
        }));
        try {
            const response = await axios.get(
                `${BASE_API}/select/material-unit`,
                { params: { material_id: materialId } },
            );
            setUnitsState((prev) => ({
                ...prev,
                items: { ...prev.items, [index]: response.data.data || [] },
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
        } catch (error) {
            console.error("Error fetching item units:", error);
            setUnitsState((prev) => ({
                ...prev,
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
        }
    };

    // Populate Data on Edit
    useEffect(() => {
        if (isEdit && initialData) {
            reset({
                name: initialData.name,
                material_id: String(initialData.material_id),
                material_unit_id: String(initialData.material_unit_id),
                quantity: Number(initialData.quantity),
                description: initialData.description || "",
                items: initialData.items.map((item) => ({
                    material_id: String(item.material_id),
                    material_unit_id: String(item.material_unit_id),
                    quantity: Number(item.quantity),
                })),
            });

            // Fetch units for items
            initialData.items.forEach((item, index) => {
                fetchItemUnits(index, item.material_id);
            });
        }
    }, [isEdit, initialData, reset]);

    const endpoint = isEdit
        ? `material-formulas/${id}/update`
        : "material-formulas/store";
    const { mutate: saveFormula, isLoading: isSubmitting } =
        usePostData(endpoint);

    const onSubmit = (data) => {
        const payload = { ...data };
        if (isEdit) {
            payload._method = "PUT";
        }

        saveFormula(payload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Formula berhasil ${isEdit ? "diperbarui" : "dibuat"}`,
                );
                navigate("/material-formulas/data");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        "Terjadi kesalahan saat menyimpan formula.",
                );
            },
        });
    };

    if (isEdit && isFetching) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner size="lg" label="Memuat formula..." />
            </div>
        );
    }

    if (isEdit && fetchError) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Formula Tidak Ditemukan"
                    description="Gagal memuat data formula. Pastikan URL benar."
                    onAction={() => navigate("/material-formulas/data")}
                    actionLabel="Kembali"
                />
            </div>
        );
    }

    const parentUnitName =
        unitsState.parent.find(
            (u) => String(u.value) === String(watch("material_unit_id")),
        )?.title || "Satuan";

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-w-0 flex-col gap-6"
        >
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div className="flex items-start gap-3">
                    <Button
                        isIconOnly
                        variant="light"
                        as={Link}
                        to="/material-formulas/data"
                        className="-ml-2"
                    >
                        <LuArrowLeft size={22} />
                    </Button>
                    <PageHeader
                        title={isEdit ? "Edit Formula" : "Buat Formula Baru"}
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            { label: "Master Data", href: "/master-data" },
                            {
                                label: "Formula Produksi",
                                href: "/material-formulas/data",
                            },
                            { label: isEdit ? "Edit" : "Buat" },
                        ]}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        color="default"
                        as={Link}
                        to="/material-formulas/data"
                        size="sm"
                    >
                        Batal
                    </Button>
                    <Button
                        color="primary"
                        type="submit"
                        isLoading={isSubmitting}
                        isDisabled={!isValid || isSubmitting}
                        startContent={!isSubmitting && <LuSave size={18} />}
                        size="sm"
                    >
                        Simpan Formula
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
                {/* Left Panel: Header Info */}
                <div className="space-y-6 lg:sticky lg:top-4 lg:col-span-1 lg:h-fit">
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-50 border-default-100 border-b px-4 py-3">
                            <ThemedText className="font-semibold" size="sm">
                                Identitas Formula
                            </ThemedText>
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4 p-4">
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: "Nama formula wajib diisi" }}
                                render={({ field }) => (
                                    <Input
                                        label="Nama Formula"
                                        placeholder="Contoh: Pakan Start Standard"
                                        variant="bordered"
                                        {...field}
                                        isInvalid={!!errors.name}
                                        errorMessage={errors.name?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="material_id"
                                control={control}
                                rules={{
                                    required: "Produk hasil wajib dipilih",
                                }}
                                render={({ field }) => (
                                    <Select
                                        label="Produk Hasil"
                                        placeholder="Pilih item"
                                        variant="bordered"
                                        isLoading={isLoadingMaterials}
                                        selectedKeys={
                                            field.value
                                                ? [String(field.value)]
                                                : []
                                        }
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            setValue("material_unit_id", ""); // Reset unit
                                        }}
                                        isInvalid={!!errors.material_id}
                                        errorMessage={
                                            errors.material_id?.message
                                        }
                                    >
                                        {(materials || []).map((m) => (
                                            <SelectItem
                                                key={m.value}
                                                value={m.value}
                                            >
                                                {m.title}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                )}
                            />

                            <div className="grid grid-cols-2 gap-3">
                                <Controller
                                    name="quantity"
                                    control={control}
                                    rules={{
                                        required: "Jumlah basis wajib diisi",
                                        min: 0.01,
                                    }}
                                    render={({ field }) => (
                                        <NumberInput
                                            hideStepper
                                            label="Qty Basis"
                                            placeholder="Contoh: 100"
                                            variant="bordered"
                                            min={0.01}
                                            {...field}
                                            isInvalid={!!errors.quantity}
                                            errorMessage={
                                                errors.quantity?.message
                                            }
                                        />
                                    )}
                                />
                                <Controller
                                    name="material_unit_id"
                                    control={control}
                                    rules={{ required: "Satuan wajib dipilih" }}
                                    render={({ field }) => (
                                        <Select
                                            label="Satuan"
                                            placeholder="Pilih"
                                            variant="bordered"
                                            isLoading={unitsState.parentLoading}
                                            selectedKeys={
                                                field.value
                                                    ? [String(field.value)]
                                                    : []
                                            }
                                            onChange={field.onChange}
                                            isDisabled={!watchMaterialId}
                                            isInvalid={
                                                !!errors.material_unit_id
                                            }
                                            errorMessage={
                                                errors.material_unit_id?.message
                                            }
                                        >
                                            {unitsState.parent.map((u) => (
                                                <SelectItem
                                                    key={u.value}
                                                    value={u.value}
                                                >
                                                    {u.title}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </div>

                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <Textarea
                                        label="Keterangan"
                                        placeholder="Catatan tambahan (opsional)"
                                        variant="bordered"
                                        {...field}
                                    />
                                )}
                            />

                            {watch("quantity") &&
                                watchMaterialId &&
                                watch("material_unit_id") && (
                                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-md p-3">
                                        <ThemedText size="xs">
                                            <span className="font-semibold">
                                                Ringkasan:
                                            </span>{" "}
                                            Formula ini mendefinisikan komposisi
                                            untuk membuat
                                            <span className="font-bold">
                                                {" "}
                                                {watch("quantity")}{" "}
                                                {parentUnitName}{" "}
                                            </span>
                                            {
                                                materials?.find(
                                                    (m) =>
                                                        String(m.value) ===
                                                        String(
                                                            watch(
                                                                "material_id",
                                                            ),
                                                        ),
                                                )?.title
                                            }
                                            .
                                        </ThemedText>
                                    </div>
                                )}
                        </CardBody>
                    </Card>
                </div>

                {/* Right Panel: Ingredients */}
                <div className="lg:col-span-2">
                    <Card
                        className="border-default-200 h-full border"
                        shadow="none"
                    >
                        <CardHeader className="bg-default-50 border-default-100 flex items-center justify-between border-b px-6 py-3">
                            <ThemedText className="font-semibold" size="sm">
                                Komposisi Bahan Baku
                            </ThemedText>
                            <Button
                                size="sm"
                                variant="flat"
                                color="primary"
                                startContent={<LuPlus />}
                                onPress={() =>
                                    append({
                                        material_id: "",
                                        material_unit_id: "",
                                        quantity: "",
                                    })
                                }
                            >
                                Tambah Bahan
                            </Button>
                        </CardHeader>
                        <CardBody className="p-0">
                            <Table
                                aria-label="Tabel Bahan Baku"
                                shadow="none"
                                classNames={{ wrapper: "rounded-none" }}
                            >
                                <TableHeader>
                                    <TableColumn>Bahan Baku</TableColumn>
                                    <TableColumn width={150}>
                                        Jumlah
                                    </TableColumn>
                                    <TableColumn width={150}>
                                        Satuan
                                    </TableColumn>
                                    <TableColumn width={50} align="center">
                                        {" "}
                                    </TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {fields.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.material_id`}
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <Select
                                                            aria-label="Pilih Bahan"
                                                            placeholder="Pilih item"
                                                            size="sm"
                                                            variant="bordered"
                                                            selectedKeys={
                                                                field.value
                                                                    ? [
                                                                          String(
                                                                              field.value,
                                                                          ),
                                                                      ]
                                                                    : []
                                                            }
                                                            onChange={(e) => {
                                                                field.onChange(
                                                                    e.target
                                                                        .value,
                                                                );
                                                                // Reset unit logic for item
                                                                setValue(
                                                                    `items.${index}.material_unit_id`,
                                                                    "",
                                                                );
                                                                fetchItemUnits(
                                                                    index,
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.material_id
                                                            }
                                                            classNames={{
                                                                trigger:
                                                                    "min-h-8 h-9",
                                                            }}
                                                        >
                                                            {(materials || [])
                                                                .filter((m) => {
                                                                    const currentId =
                                                                        String(
                                                                            m.value,
                                                                        );
                                                                    // Exclude main product
                                                                    if (
                                                                        String(
                                                                            watchMaterialId,
                                                                        ) ===
                                                                        currentId
                                                                    )
                                                                        return false;

                                                                    // Exclude items selected in other rows
                                                                    const isSelectedInOtherRow =
                                                                        watchItems.some(
                                                                            (
                                                                                item,
                                                                                i,
                                                                            ) =>
                                                                                i !==
                                                                                    index &&
                                                                                item.material_id &&
                                                                                String(
                                                                                    item.material_id,
                                                                                ) ===
                                                                                    currentId,
                                                                        );

                                                                    return !isSelectedInOtherRow;
                                                                })
                                                                .map((m) => (
                                                                    <SelectItem
                                                                        key={
                                                                            m.value
                                                                        }
                                                                        value={
                                                                            m.value
                                                                        }
                                                                    >
                                                                        {
                                                                            m.title
                                                                        }
                                                                    </SelectItem>
                                                                ))}
                                                        </Select>
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.quantity`}
                                                    control={control}
                                                    rules={{
                                                        required: true,
                                                        min: 0.0001,
                                                    }}
                                                    render={({ field }) => (
                                                        <NumberInput
                                                            hideStepper
                                                            size="sm"
                                                            variant="bordered"
                                                            aria-label="Jumlah"
                                                            placeholder="0"
                                                            min={0.0001}
                                                            {...field}
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.quantity
                                                            }
                                                            classNames={{
                                                                inputWrapper:
                                                                    "min-h-8 h-9",
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.material_unit_id`}
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <Select
                                                            aria-label="Satuan"
                                                            placeholder="Satuan"
                                                            size="sm"
                                                            variant="bordered"
                                                            isLoading={
                                                                unitsState
                                                                    .itemsLoading[
                                                                    index
                                                                ]
                                                            }
                                                            selectedKeys={
                                                                field.value
                                                                    ? [
                                                                          String(
                                                                              field.value,
                                                                          ),
                                                                      ]
                                                                    : []
                                                            }
                                                            onChange={
                                                                field.onChange
                                                            }
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]
                                                                    ?.material_unit_id
                                                            }
                                                            isDisabled={
                                                                !watchItems[
                                                                    index
                                                                ]?.material_id
                                                            }
                                                            classNames={{
                                                                trigger:
                                                                    "min-h-8 h-9",
                                                            }}
                                                        >
                                                            {(
                                                                unitsState
                                                                    .items?.[
                                                                    index
                                                                ] || []
                                                            ).map((u) => (
                                                                <SelectItem
                                                                    key={
                                                                        u.value
                                                                    }
                                                                    value={
                                                                        u.value
                                                                    }
                                                                >
                                                                    {u.title}
                                                                </SelectItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    color="danger"
                                                    variant="light"
                                                    onPress={() =>
                                                        remove(index)
                                                    }
                                                    isDisabled={
                                                        fields.length === 1
                                                    }
                                                >
                                                    <LuTrash2 size={14} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardBody>
                        <CardFooter className="justify-start px-6 py-3">
                            {errors.items && (
                                <ThemedText size="xs" className="text-danger">
                                    * Pastikan semua baris bahan baku terisi
                                    lengkap.
                                </ThemedText>
                            )}
                        </CardFooter>
                    </Card>
                </div>
            </div>
        </form>
    );
};

export default MaterialFormulaCreate;
